import React, {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Checkbox,
  Chip, Dialog, Divider, Grid,
  IconButton, ListItem, ListItemAvatar, ListItemText, Paper,
  Stack, Typography,
} from '@mui/material';
import api from '../../services/api';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import {
  DataGridPro, GridColDef, GridFilterModel,
  GridFooter, GridPaginationModel, GridRowId, GridSortModel,
} from '@mui/x-data-grid-pro';
import {datagridColors} from '../../assets/datagridStyles';
import {useGridApiRef} from '@mui/x-data-grid';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import toast from 'react-hot-toast';
import {Header} from "../AdminPages/Header";
import ReactDOM from "react-dom";
import {
  CancelRounded,
  DownloadRounded,
  ImageRounded,
  KeyboardArrowDown, KeyboardArrowRight,
  TaskAltRounded,
  VisibilityRounded
} from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import * as Icons from "@mui/icons-material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Zoom from "react-medium-image-zoom";
import Avatar from "@mui/material/Avatar";
import {toaster} from "../../services/toaster";
import {styled} from "@mui/material/styles";
import {ServiceReservationComment} from "../../components/Comment/ServiceReservationComment";
import {serviceReservationViewTable} from "../../schema/serviceReservationViewTable";
import {DetailPanel} from "../PendingChangesPage/components/DetailPanel";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import {hasPermissionByPriority} from "../../core/permissions/hasPermission";
import {ROLE_PRIORITIES} from "../../core/permissions/permissions";
import {useStateContext} from "../../hooks/useStateContext";
import {downloadAttachment} from "../../utils/downloadAttachment";

const autosizeOptions = {
  includeHeaders: true,
  includeOutliers: true,
  outliersFactor: 1.5,
  expand: true,
};

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(2),
  borderRadius: 0,
  height: '100%'
}));

const renderData = (data:any, depth = 0, mr:boolean = false) => {
  const x = mr ? 0 : 20;

  const indentStyle = {
    marginLeft: `${depth * 20}px`, // A mélység alapján növeljük a margót
  };

  if (typeof data === "object" && data !== null) {
    if (Array.isArray(data)) {
      // Ha tömb, listázd az elemeket
      return (
        <ul style={indentStyle}>
          {data.map((item, index) => (
            <li key={index}>{renderData(item, depth + 1)}</li>
          ))}
        </ul>
      );
    } else {
      // Ha objektum, jelenítsd meg a kulcs-érték párokat
      return (
        <ul style={indentStyle}>
          {Object.entries(data).map(([key, value]) => (
            <li key={key}>
              <span><b>{key}:</b> <span>{renderData(value, depth + 1)}</span></span>
            </li>
          ))}
        </ul>
      );
    }
  }
  // Ha primitív érték, egyszerűen jelenítsd meg
  return <span>{data !== null ? data.toString() : "null"}</span>;
};

export const PendingChanges: ComponentType = () => {
  const [loading, setLoading] = useState(false);
  const [openedAttachments, setOpenedAttachments] = useState<any>(false);
  const [rows, setRows] = useState<any>([]);
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const apiRef = useGridApiRef();
  const [detailCache, setDetailCache] = useState<Record<number | string, any>>({});
  const [detailLoading, setDetailLoading] = useState<Record<number | string, boolean>>({});

  useEffect(() => {
    document.title = `Zoe - Pending Changes`;
  }, []);
  const fetchRows = useCallback(() => {
    setLoading(true);

    api.get(`/pending-changes`).then((res: any) => {

      setLoading(false);

      ReactDOM.flushSync(() => {
        setRows(res);
        if (apiRef?.current) {
          apiRef.current.updateRows(res);
        }
      });

    })
      .then(() => sleep(0))
      .then(() => {
        if (apiRef?.current) {
          apiRef.current.autosizeColumns(autosizeOptions);
        }
      });
  }, [selectedOption]);

  useEffect(() => {
    fetchRows();
  }, [selectedOption]);

  const [filterModelStatic, setFilterModelStatic] = useState<GridFilterModel>({
    items: [],
  });
  const [sortModelStatic, setSortModelStatic] = useState<GridSortModel>([]);
  const [paginationModelStatic, setPaginationModelStatic] = useState<
    GridPaginationModel
  >({
    page: 0,
    pageSize: 10,
  });
  const [expandedIds, setExpandedIds] = useState<GridRowId[]>([]);

  const [selectedIds, setSelectedIds] = useState<GridRowId[]>([]);
  const [bulkSending, setBulkSending] = useState(false);
  const [rowActionLoading, setRowActionLoading] = useState<
    Record<number | string, 'approve' | 'cancel'>
  >({});

  const {user} = useStateContext();
  const isAdmin = hasPermissionByPriority(user.roles, ROLE_PRIORITIES[1]);

  const handleApprove = useCallback((id:any) => {
    // ha már fut ezen a soron egy kérés, ne indítsuk újra
    if (rowActionLoading[id]) return;
    startRowLoading(id, 'approve');
    api.post(`/pending-changes/${id}/approve`,{}).then((res: any) => {
      toast.success('Successfully approved request');
      fetchRows();
    })
      .catch(() => {
        toast.error('Error while approval request');
    }).finally(()=>{
      stopRowLoading(id);
    });
  }, [rowActionLoading]);
  const handleCancel = useCallback((id:any) => {
    // ha már fut ezen a soron egy kérés, ne indítsuk újra
    if (rowActionLoading[id]) return;
    startRowLoading(id, 'cancel');
    api.post(`/pending-changes/${id}/cancel`,{}).then((res: any) => {
      toast.success('Successfully canceled request');
      fetchRows();
    })
      .catch(() => {
        toast.error('Error while cancel request');
    }).finally(()=>{
      stopRowLoading(id);
    });
  }, [rowActionLoading]);

  const startRowLoading = (id: number | string, action: 'approve' | 'cancel') =>
    setRowActionLoading(prev => ({ ...prev, [id]: action }));

  const stopRowLoading = (id: number | string) =>
    setRowActionLoading(prev => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });

  const toggleRow = useCallback((id: GridRowId) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const columns: GridColDef[] = useMemo(() => {
    const columnArray: GridColDef[] = [

      {
        field: 'serial_number',
        type: 'string',
        headerName: '',
        cellClassName: '',
        editable: false,
        width: 50,
        renderCell: (params: any) => {
          const currentPage = paginationModelStatic.page;
          const pageSize = paginationModelStatic.pageSize;

          const rowIndex =
            currentPage * pageSize +
            params.api.getRowIndexRelativeToVisibleRows(params.id) +
            1;

          const id = params.id;
          return (
            <>
              {rowIndex}
            </>
          );
        },
        renderHeaderFilter: () => {
          return <></>;
        },
      },
      {
        field: 'model_type',
        headerName: 'Type',
        editable: false,
        type: 'string',
        minWidth: 125,
      },
      {
        field: 'model_id',
        headerName: 'ID',
        editable: false,
        type: 'string',
        minWidth: 125,
      },
      {
        field: 'changeable',
        headerName: 'Name',
        editable: false,
        type: 'string',
        minWidth: 125,
        flex: 1,
        renderCell: (params) =>{
          const hasLink = params?.row?.link;
          return hasLink ? (
              <a
                href={hasLink}
                target={'_blank'}
              >
                {params.value?.name}
              </a>

            ) : params.value?.name;
        },
      },
    /*  {
        field: 'changes',
        headerName: 'Changes',
        editable: false,
        type: 'string',
        minWidth: 125,
        renderCell: (params) =>{
          const IconComponent = Icons[params.row?.changes?.service_reservation_status?.icon?.name as keyof typeof Icons];
          const color = params.row?.changes?.service_reservation_status?.color?.color;
          const name= params.row?.changes?.service_reservation_status?.name;

          return (name ?
            <Chip
              variant="outlined"
              style={{
                color: color,
                borderColor: color
            }}
              icon={IconComponent ? <IconComponent color={color} /> : <></>}
              label={name}
            /> : <>
              {(params.row.action === 'update' && params.row?.changes) ? renderData(params.row?.changes):''}</>
          );
        }
      },*/
      {
        field: 'action',
        headerName: 'Action',
        editable: false,
        type: 'string',
        minWidth: 125,
        renderCell: (params) => {
          const value = params.value;

          let color:any = 'error';
          if (value === 'delete') color = 'error';
          else if (value === 'update') color = 'info';
          else if (value === 'create') color = 'primary';

          return <Chip label={value} color={color} size="small" />;
        }
      },
      {
        field: 'requester',
        headerName: 'Requester',
        editable: false,
        type: 'string',
        minWidth: 125,
        valueGetter: (params) =>
          params.value?.name,
      },
      {
        field: 'message',
        headerName: 'Message',
        editable: false,
        type: 'string',
        minWidth: 125,
      },
      {
        field: 'files',
        headerName: 'Attachments',
        editable: false,
        type: 'string',
        width: 100,
        maxWidth: 100,
        renderCell: (params: any) => {
          return (
            <>
              <Stack direction={'row'}>
                <Tooltip title={'Show attachments'} >
                  <IconButton onClick={()=>setOpenedAttachments(params.row)}>
                    <VisibilityRounded color={'primary'} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </>
          );
        },
      },
      {
        field: 'actions',
        type: 'string',
        headerName: '',
        cellClassName: '',
        editable: false,
        width: 100,
        maxWidth: 100,
        renderCell: (params: any) => {

          const id = params.row.id;
          const isRowLoading = !!rowActionLoading[id];

          if (isRowLoading || bulkSending) {
            return (
              <Stack direction="row" alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
                <CircularProgress size={20} />
              </Stack>
            );
          }

          return (
            <>
              <Stack direction={'row'}>
                {isAdmin && (
                  <>
                    <Tooltip title={'Approve'} >
                      <IconButton
                        disabled={isRowLoading}
                        onClick={()=>handleApprove(params.row.id)}
                      >
                        <TaskAltRounded color={'success'} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={'Cancel'} >
                      <IconButton
                        disabled={isRowLoading}
                        onClick={()=>handleCancel(params.row.id)}
                      >
                        <CancelRounded color={'error'} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
            </>
          );
        },
        renderHeaderFilter: () => {
          return <></>;
        },
      },
    ];

    return columnArray;
  }, [
    rows,
    sortModelStatic,
    filterModelStatic,
    paginationModelStatic,
    apiRef,
    expandedIds,
    toggleRow,
    rowActionLoading,
    bulkSending,
    isAdmin
  ]);

  const fetchDetailForRow = useCallback(
    async (id: number | string) => {
      // ha már van a cache-ben, ne tölts újra
      if (detailCache[id]) return;

      setDetailLoading((prev) => ({ ...prev, [id]: true }));
      try {
        // tetszőleges endpoint: igazítsd a sajátodra
        const data: any = await api.get(`/pending-changes/${id}`);
        setDetailCache((prev) => ({ ...prev, [id]: data }));

        // ha a DataGrid rows-ában is szeretnél frissíteni valamit:
        setRows((prev: any) =>
          prev.map((r: any) => (r.id === id ? { ...r, ...data } : r))
        );
      } catch (e) {
        toast.error('Error while loading details');
      } finally {
        setDetailLoading((prev) => ({ ...prev, [id]: false }));
      }
    },
    [detailCache]
  );

  const CustomFooter = () => {
    const hasSelection = selectedIds.length > 0;

    return (
      <Stack
        direction={'row'}
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 8px',
        }}
      >
        <span style={{ visibility: 'hidden' }}>.</span>

        <GridFooter />
      </Stack>
    );
  };

  function CustomSortIconDesc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropDownRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIconAsc(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  function CustomSortIcon(props: any) {
    return (
      <div
        style={{
          background: '#42948c',
          borderRadius: '40px',
          height: '15px',
          width: '15px',
        }}
      >
        <ArrowDropUpRoundedIcon
          color={'secondary'}
          sx={{
            width: '15px',
            height: '15px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </div>
    );
  }

  const handleExpandedChange = useCallback((ids: GridRowId[]) => {
    // mely ID-k nyíltak meg most (amik eddig nem voltak benne)?
    const newlyOpened = ids.filter((id) => !expandedIds.includes(id));
    newlyOpened.forEach((id) => fetchDetailForRow(id));
    setExpandedIds(ids);
  }, [expandedIds, fetchDetailForRow]);

  const handleFilterModelChange = useCallback((newModel: any) => {
    setFilterModelStatic((prevModel) =>
      JSON.stringify(prevModel) === JSON.stringify(newModel)
        ? prevModel
        : newModel
    );
  }, []);

  const handleSortModelChange = useCallback((newModel: any) => {
    setSortModelStatic((prevModel) =>
      JSON.stringify(prevModel) === JSON.stringify(newModel)
        ? prevModel
        : newModel
    );
  }, []);

  const handlePaginationModelChange = useCallback((newModel: any) => {
    setPaginationModelStatic((prevModel) =>
      JSON.stringify(prevModel) === JSON.stringify(newModel)
        ? prevModel
        : newModel
    );
  }, []);

  const handleDownload = async (attachment: any) => {
    try {
      await downloadAttachment(attachment, api.getFile);
    } catch (error) {
      console.log(error);
      toaster.show({
        icon: 'error',
        message: `Error while download data`,
        intent: 'danger',
      });
    }
    return true;
  };

  const handleBulkSend = useCallback(async (all:any) => {
    if (!selectedIds.length) return;
    try {
      setBulkSending(true);

      const ids = all ? {} : { ids: selectedIds };

      await api.post(`/pending-changes/all`, ids);
      toast.success(`${selectedIds.length} line approval`);
      setSelectedIds([]);
      fetchRows(); // már megvan fent
    } catch (e) {
      toast.error('Error while approving lines');
    } finally {
      setBulkSending(false);
    }
  }, [selectedIds, fetchRows]);

  return (
    <>
      <Header
        title={'Approvals'}
        rowCount={rows.length}
        hasPagination={false}
        refreshTableData={()=>{
          fetchRows();
        }}
        selectedIds={selectedIds}
        bulkSending={bulkSending}
        onBulkSend={(all)=>{
          handleBulkSend(all);
        }}
        hasAllBulkSendButton
      />

      <Box sx={datagridColors('calc(100vh - 145px)')}>
        <DataGridPro

          getDetailPanelContent={(params) => <DetailPanel
            row={params.row}
          />}
          getDetailPanelHeight={(/* params */) => 400} // vagy számold dinamikusan
          detailPanelExpandedRowIds={expandedIds}
          onDetailPanelExpandedRowIdsChange={handleExpandedChange}
          apiRef={apiRef}
          sx={{
            pointerEvents: loading ? 'none' : 'auto',
            opacity: loading ? 0.5 : 1,
            maxHeight: 'calc(100% - 70px)',
          }}
          rows={rows}
          getRowClassName={(params: any) => {
            const color = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
            const isOpen = openedAttachments && openedAttachments.id === params.id;
            return `${color} ${isOpen ? 'attachments-open' : ''}`;
          }}
          disableColumnMenu={true}
          columns={columns}
          loading={loading}
          columnHeaderHeight={38}
          getRowHeight={()=>'auto'}
          pagination
          slots={{
            // @ts-ignore
            columnSortedDescendingIcon: CustomSortIconDesc,
            columnSortedAscendingIcon: CustomSortIconAsc,
            columnUnsortedIcon: CustomSortIcon,
            footer: CustomFooter,
            headerFilterMenu: null,
          }}
          unstable_headerFilters={false}
          pageSizeOptions={[25, 50, 100, {label: 'All', value: rows.length}]}
          initialState={{
            pinnedColumns: { left: ['serial_number'], right: ['actions'] },
          }}
          filterMode={'client'}
          disableChildrenFiltering={true}
          onFilterModelChange={handleFilterModelChange}
          sortingMode={'client'}
          onSortModelChange={handleSortModelChange}
          disableMultipleColumnsSorting={false}
          onPaginationModelChange={handlePaginationModelChange}

          checkboxSelectionVisibleOnly
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectedIds}
          onRowSelectionModelChange={(newSelection) => setSelectedIds(newSelection)}
        />
      </Box>

      {openedAttachments && (
        <>
          <Dialog
            open={openedAttachments}
            onClose={() => {
              setOpenedAttachments(false);
            }}
          >
            <DialogTitle>
              Attachments
            </DialogTitle>

            <IconButton
              aria-label="close"
              onClick={()=>{
                setOpenedAttachments(false);
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>

            <DialogContent>

              {(!openedAttachments?.files || !openedAttachments.files.length) && (
                <>
                  <ListItem
                    sx={{
                      padding: '10px',
                      background: '#f9f9f9',
                      borderRadius: '10px'
                    }}
                  >
                    <ListItemText
                      primary={<>No attachments</>}
                    />
                  </ListItem>
                </>
              )}

              {openedAttachments?.files.map((file:any, idx:any) => {
                const filename = file?.name;
                const fileUrl = file.link;
                const isImage = /\.(jpg|jpeg|png|gif)$/i.test(fileUrl);
                const isPdf = /\.pdf$/i.test(fileUrl);

                const handleFileClick = () => {
                  if (isImage || isPdf) {
                    window.open(fileUrl, '_blank'); // új lapon nyit
                  } else {
                    const link = document.createElement('a');
                    link.href = fileUrl;
                    link.download = filename;
                    link.click();
                  }
                };

                return (
                  <ListItem
                    sx={{
                      paddingRight: '88px',
                      background: '#f9f9f9',
                      borderRadius: '10px'
                    }}
                    secondaryAction={
                      <Stack direction={'row'}>
                        <Tooltip title="Download file">
                          <IconButton
                            onClick={() => {
                              handleDownload(file);
                            }}
                            size={'small'}
                            edge="end"
                            aria-label="download"
                          >
                            <DownloadRounded fontSize={'small'}/>
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                  >
                    <ListItemAvatar>
                      {isImage ? (
                        <Zoom>
                          <Avatar src={isImage ? fileUrl : ''}/>
                        </Zoom>
                      ) : (
                        <>
                          <Avatar>
                            <ImageRounded/>
                          </Avatar>
                        </>
                      )}
                    </ListItemAvatar>
                    <ListItemText
                      onClick={()=>{
                        handleFileClick();
                      }}
                      sx={{
                        '&:hover':{
                          cursor: 'pointer'
                        }
                      }}
                      primary={filename}
                      secondary={`Created: ${file?.created_at}`}
                    />
                  </ListItem>
                );
              })}

            </DialogContent>
          </Dialog>
        </>
      )}

    </>
  );
};

function sleep(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
